import { useEffect, useState, useRef } from "react";
import { ILocationProps } from "../../services/Location/types";
import { MapContainer, TileLayer, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import DriftMarker from "react-leaflet-drift-marker";
import { IconButton, Tooltip } from "@mui/material";
import { t } from "i18next";
import locationIcon from "assets/images/icons/map/locationIcon.png";
import locationsIcon from "assets/images/icons/map/locationsIcon.png";
import L from "leaflet";
import StopIcon from "@mui/icons-material/Stop";
import { useLocations } from "../../pages/LocationPage/_components/LocationContext";

interface MapViewProps {
    selectedId?: string | null;
    sidebarOpen: boolean;
    selectedIds: string[];
}

function MapController({
    selectedId,
    locations,
    isFollowing,
}: {
    selectedId?: string | null;
    locations: ILocationProps[];
    isFollowing: boolean;
}) {
    const map = useMap();

    useEffect(() => {
        if (selectedId && isFollowing) {
            const loc = locations.find(l => l.entityId === selectedId);
            if (loc) {
                map.closePopup();
                map.flyTo([loc.latitude, loc.longitude], 17, {
                    animate: true,
                    duration: 1,
                });
            }
        }
    }, [selectedId, locations, map, isFollowing]);

    return null;
}

function ResizeController({ sidebarOpen }: { sidebarOpen: boolean }) {
    const map = useMap();

    useEffect(() => {
        const timer = setTimeout(() => {
            map.invalidateSize();
            map.setView(map.getCenter(), map.getZoom(), {
                animate: true,
                duration: 0.5,
            });
        }, 1);

        return () => clearTimeout(timer);
    }, [sidebarOpen, map]);

    return null;
}

function InitialBoundsController({ locations }: { locations: ILocationProps[] }) {
    const map = useMap();
    const hasFitted = useRef(false);

    useEffect(() => {
        if (!hasFitted.current && locations.length > 0) {
            const bounds = L.latLngBounds(
                locations.map(loc => [loc.latitude, loc.longitude])
            );
            map.fitBounds(bounds, { padding: [50, 50] });
            hasFitted.current = true;
        }
    }, [locations, map]);

    return null;
}

export default function MapView({ selectedId, sidebarOpen = true, selectedIds }: MapViewProps) {
    const locations = useLocations();
    const [showAll, setShowAll] = useState(true);
    const [isFollowing, setIsFollowing] = useState(true);

    useEffect(() => {
        if (selectedId) {
            setIsFollowing(true);
        }
    }, [selectedId]);

    const defaultIcon = L.icon({
        iconUrl: require("assets/images/icons/map/vanIcon.png"),
        iconRetinaUrl: require("assets/images/icons/map/vanIcon.png"),
        iconSize: [40, 41],
        iconAnchor: [12, 41],
        popupAnchor: [8, -34],
        shadowSize: [41, 41],
    });

    const markersToRender = showAll
        ? locations.filter(l => selectedIds.includes(l.entityId))
        : selectedId
            ? locations.filter(l => l.entityId === selectedId)
            : [];

    return (
        <MapContainer center={[0, 0]} zoom={2} className="map-container" style={{ height: "100%", width: "100%" }}>
            <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />

            {markersToRender.map((loc) => (
                <DriftMarker
                    key={loc.entityId}
                    position={[loc.latitude, loc.longitude]}
                    duration={1000}
                    icon={defaultIcon}
                >
                    <Popup autoClose={false} closeButton={true}>
                        <div style={{
                            fontFamily: "Inter, Roboto, sans-serif",
                            fontSize: "11px",
                            lineHeight: 1.3,
                            color: "#333",
                            padding: "2px 4px",
                        }}>
                            <div style={{ fontWeight: 600 }}>{loc.entityId}</div>
                            <div style={{ color: "#777" }}>
                                {loc.latitude.toFixed(4)}, {loc.longitude.toFixed(4)}
                            </div>
                        </div>
                    </Popup>
                </DriftMarker>
            ))}

            <InitialBoundsController locations={markersToRender} />

            <MapController selectedId={selectedId} locations={locations} isFollowing={isFollowing} />
            <ResizeController sidebarOpen={sidebarOpen} />

            {selectedId && (
                <>
                    {isFollowing && (
                        <Tooltip title={t("map.stopFollow")}>
                            <IconButton
                                onClick={() => setIsFollowing(false)}
                                sx={{
                                    position: "absolute",
                                    top: 135,
                                    left: 5,
                                    zIndex: 1000,
                                    backgroundColor: "white",
                                    boxShadow: 2,
                                    borderRadius: "50%",
                                    width: 48,
                                    height: 48,
                                    "&:hover": { backgroundColor: "white", opacity: 1 },
                                }}
                            >
                                <StopIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </>
            )}
        </MapContainer>
    );
}
